import React from 'react';
import { Column, Pie } from '@ant-design/plots';
import { Card, Row, Col, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { formatString, NoDataPlaceholder } from '../../../utils/helpers';
import { Radio } from 'antd';

type CampaignProps = {
    campaignData: any[];
    earningsOverTime: {
        granularity: 'day' | 'month';
        data: {
            label: string;
            value: number;
            rolling7d?: number;
        }[];
    };
    onGranularityChange?: (g: 'day' | 'month') => void;
};

const FONT = 'Poppins';

const CampaignsOverviewGraph = ({ campaignData, earningsOverTime, onGranularityChange }: CampaignProps) => {
    const { granularity, data } = earningsOverTime || { granularity: 'month', data: [] };
    
    const formatLabel = (label: string, granularity: 'day' | 'month') => {
        if (granularity === 'month') {
            const [year, month] = label.split('-').map(Number);
            return new Date(year, month - 1).toLocaleString('en-US', {
                month: 'long'
            });
        }

        const date = new Date(label);
        return date.toLocaleDateString('en-US', {
            day: '2-digit',
            month: 'short'
        });
    };
    
    const formattedData = data.map((item) => ({
        label: formatLabel(item.label, granularity),
        Earnings: item.value,
        rolling7d: item.rolling7d
    }));

    const earningsConfig = {
        data: formattedData,
        xField: 'label',
        yField: 'Earnings',
        shapeField: 'column25D',
        style: { fill: 'rgba(126, 212, 236, 0.8)' },
        height: 320,
        autoFit: true,
        axis: {
            x: {
                labelFontFamily: FONT,
                labelAutoHide: true,
                labelAutoRotate: true
            },
            y: { labelFontFamily: FONT }
        },
        tooltip: {
            title: { style: { fontFamily: FONT } },
            itemName: { style: { fontFamily: FONT } },
            itemValue: {
                formatter: (v: number) => `$${v.toFixed(2)}`
            }
        }
    };
    
    const formattedCampaignData = campaignData?.map((item: any) => ({
        ...item,
        campaign_status: formatString(item.campaign_status),
        Campaigns: item.numberOfCampaigns ?? 0
    }));

    const configCampaignStatus = {
        data: formattedCampaignData || [],
        angleField: 'Campaigns',
        colorField: 'campaign_status',
        legend: {
            itemName: {
                formatter: (text: string) => formatString(text),
                style: { fontFamily: FONT }
            }
        },
        height: 320,
        autoFit: true,
        tooltip: {
            title: { style: { fontFamily: FONT } },
            itemName: { style: { fontFamily: FONT } },
            itemValue: { style: { fontFamily: FONT } }
        }
    };

    const EarningsCustomizedCardTitle = () => (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Earnings over time</span>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {onGranularityChange && (
                    <Radio.Group
                        size="small"
                        value={granularity}
                        onChange={(e) => {
                            e.preventDefault();
                            onGranularityChange?.(e.target.value);
                        }}
                    >
                        <Radio.Button value="day">Day</Radio.Button>
                        <Radio.Button value="month">Month</Radio.Button>
                    </Radio.Group>
                )}

                <Tooltip title="Money generated from clicks over time">
                    <InfoCircleOutlined />
                </Tooltip>
            </div>
        </div>
    );

    const CampaignOverviewCustomizedCardTitle = () => (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Campaigns overview</span>
            <Tooltip title="Number of campaigns categorized by status">
                <InfoCircleOutlined />
            </Tooltip>
        </div>
    );

    return (
        <Card>
            <Row gutter={[16, 16]}>
                <Col xs={24} md={16}>
                    <Card title={<EarningsCustomizedCardTitle />}>
                        {formattedData.length > 0 ? (
                            <Column {...earningsConfig} />
                        ) : (
                            <NoDataPlaceholder message="No earnings data available" />
                        )}
                    </Card>
                </Col>

                <Col xs={24} md={8}>
                    <Card title={<CampaignOverviewCustomizedCardTitle />}>
                        {formattedCampaignData.length > 0 ? (
                            <Pie {...configCampaignStatus} />
                        ) : (
                            <NoDataPlaceholder message="No campaign data to display" />
                        )}
                    </Card>
                </Col>
            </Row>
        </Card>
    );
};

export default CampaignsOverviewGraph;